import { type ReactNode, useMemo } from 'react';

import { DataProvider } from '@visx/xychart';

import { type ThemeConfig, createSambaChartTheme } from './theme';

import type { Scale } from './axis/useXYScales';

export type ChartProviderProps = {
  children: ReactNode;
  theme?: Partial<ThemeConfig>;
  xScale: Scale;
  yScale: Scale;
  horizontal?: boolean | 'auto';
};

/**
 * Provides chart context and theme configuration for child components.
 *
 * Use this when you need to:
 * - Share scales and theme across multiple XYChart components
 * - Add a Legend outside of the XYChart container
 *
 * For single charts without external legends, you can pass xScale/yScale/theme directly to XYChart.
 *
 * @example
 * ```tsx
 * const { xScale, yScale } = useXYScales({ yMin: 0, yMax: 100 });
 *
 * <ChartProvider xScale={xScale} yScale={yScale} theme={{ colors: ['#1E88E5'] }}>
 *   <Legend config={{ series1: { label: 'My Data' } }} />
 *   <XYChart width={500} height={300}>
 *     <AreaSeries data={data} dataKey="series1" xAccessor={d => d.x} yAccessor={d => d.y} />
 *   </XYChart>
 * </ChartProvider>
 * ```
 */
export const ChartProvider = ({ children, theme, xScale, yScale, horizontal }: ChartProviderProps) => {
  const chartTheme = useMemo(() => createSambaChartTheme(theme), [theme]);

  return (
    <DataProvider xScale={xScale} yScale={yScale} theme={chartTheme} horizontal={horizontal}>
      {children}
    </DataProvider>
  );
};
